/**
 * Générateur — Arrondir un nombre non-décimal (π, fractions) à un rang demandé
 * Attendu ID : 607 | Niveau : 6ème | Domaine : NC
 *
 * Types générés : qcm_unique, qcm_multiple, texte (saisie), tableau_vf, relier, texte_trou_drag
 * Rangs au programme : unité, dixième, centième (millième HORS PROGRAMME).
 *
 * Règles dures de la fiche cluster 5 (section ID 607) :
 *   - Pool des non-décimaux : π, 1/3, 2/3, 1/6, 1/7, 1/9, 2/9, 35/33
 *   - Cœur Eduscol : π et 1/3 (privilégiés dans les générateurs)
 *   - Développement affiché : EXACTEMENT 4 décimales suivies de '…' (ligne 217)
 *   - Aucun distracteur ne doit être un arrondi au millième (ligne 265)
 *   - KaTeX via élément expression pour les fractions (ligne 267)
 *
 * Variantes estimées : >1000
 */

import {
    randomChoice, randomChoiceMultiple, shuffle,
    makeQcm, makeSaisie, makeVraiFaux, makeTexteTrou, tex,
} from '../../../utils';

// ─── Constantes ───
const ATTENDU_ID = 607;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const RANGS = [
    { key: 'unite', libelle: "à l'unité", decimals: 0 },
    { key: 'dixieme', libelle: 'au dixième', decimals: 1 },
    { key: 'centieme', libelle: 'au centième', decimals: 2 },
];

/**
 * Pool des non-décimaux. Chaque entrée fournit :
 *   - symbol    : notation textuelle (utilisée dans les phrases et les pairs Relier)
 *   - katex     : formule KaTeX (pour l'élément expression)
 *   - dev4      : développement à 4 décimales exactes (affiché avec '…')
 *   - arrondis  : arrondis vérifiés aux 3 rangs au programme
 *   - confDenom : arrondi de confusion dénominateur (ex: 1/3 → 1/30 → 0,03)
 */
const NON_DEC = [
    {
        name: 'π',
        symbol: 'π',
        katex: '\\pi',
        dev4: '3,1415',
        arrondis: { unite: '3', dixieme: '3,1', centieme: '3,14' },
        // Pas de "confusion dénominateur" pertinente pour π
        confDenom: null,
    },
    {
        name: '1/3',
        symbol: '1/3',
        katex: '\\dfrac{1}{3}',
        dev4: '0,3333',
        arrondis: { unite: '0', dixieme: '0,3', centieme: '0,33' },
        confDenom: '0,03', // confusion 1/3 ↔ 1/30
    },
    {
        name: '2/3',
        symbol: '2/3',
        katex: '\\dfrac{2}{3}',
        dev4: '0,6666',
        arrondis: { unite: '1', dixieme: '0,7', centieme: '0,67' },
        confDenom: '0,06', // confusion 2/3 ↔ 2/30
    },
    {
        name: '1/6',
        symbol: '1/6',
        katex: '\\dfrac{1}{6}',
        dev4: '0,1666',
        arrondis: { unite: '0', dixieme: '0,2', centieme: '0,17' },
        confDenom: null,
    },
    {
        name: '1/7',
        symbol: '1/7',
        katex: '\\dfrac{1}{7}',
        dev4: '0,1428',
        arrondis: { unite: '0', dixieme: '0,1', centieme: '0,14' },
        confDenom: null,
    },
    {
        name: '1/9',
        symbol: '1/9',
        katex: '\\dfrac{1}{9}',
        dev4: '0,1111',
        arrondis: { unite: '0', dixieme: '0,1', centieme: '0,11' },
        confDenom: null,
    },
    {
        name: '2/9',
        symbol: '2/9',
        katex: '\\dfrac{2}{9}',
        dev4: '0,2222',
        arrondis: { unite: '0', dixieme: '0,2', centieme: '0,22' },
        confDenom: null,
    },
    {
        name: '35/33',
        symbol: '35/33',
        katex: '\\dfrac{35}{33}',
        dev4: '1,0606',
        arrondis: { unite: '1', dixieme: '1,1', centieme: '1,06' },
        confDenom: null,
    },
    // ─── Extensions : septièmes et onzièmes (familles non-décimales) ───
    {
        name: '2/7',
        symbol: '2/7',
        katex: '\\dfrac{2}{7}',
        dev4: '0,2857',
        arrondis: { unite: '0', dixieme: '0,3', centieme: '0,29' },
        confDenom: null,
    },
    {
        name: '5/7',
        symbol: '5/7',
        katex: '\\dfrac{5}{7}',
        dev4: '0,7142',
        arrondis: { unite: '1', dixieme: '0,7', centieme: '0,71' },
        confDenom: null,
    },
    {
        name: '1/11',
        symbol: '1/11',
        katex: '\\dfrac{1}{11}',
        dev4: '0,0909',
        arrondis: { unite: '0', dixieme: '0,1', centieme: '0,09' },
        confDenom: null,
    },
    {
        name: '3/11',
        symbol: '3/11',
        katex: '\\dfrac{3}{11}',
        dev4: '0,2727',
        arrondis: { unite: '0', dixieme: '0,3', centieme: '0,27' },
        confDenom: null,
    },
    {
        name: '5/11',
        symbol: '5/11',
        katex: '\\dfrac{5}{11}',
        dev4: '0,4545',
        arrondis: { unite: '0', dixieme: '0,5', centieme: '0,45' },
        confDenom: null,
    },
    // ─── Extensions supplémentaires (septièmes + onzièmes) ───
    {
        name: '4/7',
        symbol: '4/7',
        katex: '\\dfrac{4}{7}',
        dev4: '0,5714',
        arrondis: { unite: '1', dixieme: '0,6', centieme: '0,57' },
        confDenom: null,
    },
    {
        name: '6/7',
        symbol: '6/7',
        katex: '\\dfrac{6}{7}',
        dev4: '0,8571',
        arrondis: { unite: '1', dixieme: '0,9', centieme: '0,86' },
        confDenom: null,
    },
    {
        name: '2/11',
        symbol: '2/11',
        katex: '\\dfrac{2}{11}',
        dev4: '0,1818',
        arrondis: { unite: '0', dixieme: '0,2', centieme: '0,18' },
        confDenom: null,
    },
    {
        name: '4/11',
        symbol: '4/11',
        katex: '\\dfrac{4}{11}',
        dev4: '0,3636',
        arrondis: { unite: '0', dixieme: '0,4', centieme: '0,36' },
        confDenom: null,
    },
    {
        name: '6/11',
        symbol: '6/11',
        katex: '\\dfrac{6}{11}',
        dev4: '0,5454',
        arrondis: { unite: '1', dixieme: '0,5', centieme: '0,55' },
        confDenom: null,
    },
    {
        name: '7/11',
        symbol: '7/11',
        katex: '\\dfrac{7}{11}',
        dev4: '0,6363',
        arrondis: { unite: '1', dixieme: '0,6', centieme: '0,64' },
        confDenom: null,
    },
    {
        name: '8/11',
        symbol: '8/11',
        katex: '\\dfrac{8}{11}',
        dev4: '0,7272',
        arrondis: { unite: '1', dixieme: '0,7', centieme: '0,73' },
        confDenom: null,
    },
];

/** Noms des non-décimaux cœur Eduscol (fiche ligne 222). */
const CORE_EDUSCOL = ['π', '1/3'];

// ─── Labels ───
const L_QCM = "Trouver l'arrondi à un rang d'une fraction non décimale";
const L_QCM_MULTI = 'Affirmations correctes sur les arrondis de non-décimaux';
const L_SAISIE = "Saisir l'arrondi d'un non-décimal";
const L_VF = "Vrai/Faux sur des affirmations d'arrondi de non-décimaux";
const L_TROU = "Compléter un tableau d'arrondis de non-décimaux";

// ─── Helpers ───

const expr = (katex) => ({ type: 'expression', config: { contenu: katex } });

/** Le non-décimal tel que l'élève le LIT : π en clair, une fraction en
 *  KaTeX vertical (audit fractions en texte, 2026-09-06). `symbol` (« 1/3 »)
 *  ne sert plus qu'aux clés et aux tests. */
const symb = (nd) => (nd.name === 'π' ? 'π' : tex(nd.katex));
/** Même chose en style TEXTE (`\frac`) pour un rappel écrit dans une phrase :
 *  la grande fraction cassait la ligne de l'énoncé (retour écran 2026-09-06). */
const symbInline = (nd) => (nd.name === 'π' ? 'π' : tex(nd.katex.replace('\\dfrac', '\\frac')));
/** « a, b et c » */
const enumFr = (items) => (items.length <= 1 ? items.join('') : `${items.slice(0, -1).join(', ')} et ${items[items.length - 1]}`);

const pickCore = () => randomChoice(NON_DEC.filter((x) => CORE_EDUSCOL.includes(x.name)));

/** Pick with a probability boost for cœur Eduscol non-decimals. */
const pickNonDec = (coreBias = 0.5) => {
    if (Math.random() < coreBias) return pickCore();
    return randomChoice(NON_DEC);
};

// ═══════════════════════════════════════════════════════════
// 1. QCM UNIQUE — "Quel est l'arrondi au X de Y ?" (Y non-décimal)
// ═══════════════════════════════════════════════════════════

const genNonDecQcm = () => {
    // Biais cœur Eduscol réduit de 0.55 → 0.30 pour gagner en variété visuelle.
    // Le piège confDenom (1/3, 2/3) reste activé ~30% des tirages (largement suffisant).
    const nd = pickNonDec(0.30);
    const rang = randomChoice(RANGS);
    const correct = nd.arrondis[rang.key];

    // Distracteurs — familles du pool fiche ligne 235-245
    const distractors = new Set();

    // (a) Rang décalé (pool ligne 239/241) — arrondis au rang d'à côté
    for (const other of RANGS) {
        if (other.key !== rang.key) distractors.add(nd.arrondis[other.key]);
    }

    // (b) Confusion dénominateur (pool ligne 235) — si disponible pour ce non-dec
    if (nd.confDenom) distractors.add(nd.confDenom);

    // (c) Troncature au rang cible (pool ligne 234) — seulement si règle du 5 s'applique
    if (rang.key === 'centieme') {
        const trunc = nd.dev4.slice(0, 4); // "0,3333" → "0,33"
        if (trunc !== correct) distractors.add(trunc);
    } else if (rang.key === 'dixieme') {
        const trunc = nd.dev4.slice(0, 3); // "0,3333" → "0,3"
        if (trunc !== correct) distractors.add(trunc);
    }

    // Retirer la bonne réponse
    distractors.delete(correct);

    // Compléter avec arrondis d'autres non-décimaux si besoin
    const otherArr = shuffle(NON_DEC.filter((x) => x !== nd));
    for (const other of otherArr) {
        if (distractors.size >= 3) break;
        const v = other.arrondis[rang.key];
        if (v !== correct) distractors.add(v);
    }

    const distArr = [...distractors].slice(0, 3);
    const choix = [correct, ...distArr];

    const payload = makeQcm(
        choix,
        correct,
        `<p>On sait que {{element:0}} ≈ ${nd.dev4}…</p><p>Quel est l'arrondi ${rang.libelle} de {{element:0}} ?</p>`,
        opts(L_QCM),
    );
    payload.elements_additionnels = { version: 1, elements: [expr(nd.katex)] };
    return payload;
};

// ═══════════════════════════════════════════════════════════
// 2. QCM MULTIPLE — Affirmations correctes
// ═══════════════════════════════════════════════════════════

/** Pool d'affirmations VRAIES (arrondis corrects). */
const buildVraiPool = () => {
    const pool = [];
    for (const nd of NON_DEC) {
        for (const rang of RANGS) {
            pool.push({
                text: `L'arrondi ${rang.libelle} de ${symb(nd)} est ${nd.arrondis[rang.key]}`,
                nd,
                core: CORE_EDUSCOL.includes(nd.name),
            });
        }
    }
    return pool;
};

/** Pool d'affirmations FAUSSES construites procéduralement. */
const buildFauxPool = () => {
    const pool = [];
    // (a) Égalités strictes (toujours fausses)
    for (const nd of NON_DEC) {
        for (const rang of [RANGS[1], RANGS[2]]) {
            pool.push({
                text: `${symb(nd)} = ${nd.arrondis[rang.key]}`,
                family: 'egalite_stricte',
                nd,
            });
        }
    }
    // (b) Troncatures au centième (piège règle du 5)
    for (const nd of NON_DEC) {
        const trunc = nd.dev4.slice(0, 4);
        if (trunc !== nd.arrondis.centieme) {
            pool.push({
                text: `L'arrondi au centième de ${symb(nd)} est ${trunc}`,
                family: 'troncature_centieme',
                nd,
            });
        }
    }
    // (c) Troncatures au dixième
    for (const nd of NON_DEC) {
        const trunc = nd.dev4.slice(0, 3);
        if (trunc !== nd.arrondis.dixieme) {
            pool.push({
                text: `L'arrondi au dixième de ${symb(nd)} est ${trunc}`,
                family: 'troncature_dixieme',
                nd,
            });
        }
    }
    // (d) Rangs décalés (ex: arrondi au dixième donné = arrondi au centième correct)
    for (const nd of NON_DEC) {
        pool.push({
            text: `L'arrondi au dixième de ${symb(nd)} est ${nd.arrondis.centieme}`,
            family: 'rang_decale',
            nd,
        });
    }
    return pool;
};

const VRAI_POOL = buildVraiPool();
const FAUX_POOL = buildFauxPool();

const genNonDecQcmMultiple = () => {
    // 3 VRAI + 2 FAUX
    // Contrainte : ≥1 cœur Eduscol dans les VRAIS, ≥1 égalité stricte dans les FAUX
    const coreVrais = VRAI_POOL.filter((v) => v.core);
    const nonCoreVrais = VRAI_POOL.filter((v) => !v.core);
    const egaliteFaux = FAUX_POOL.filter((f) => f.family === 'egalite_stricte');
    const autreFaux = FAUX_POOL.filter((f) => f.family !== 'egalite_stricte');

    // Sélection des vrais : 2 cœur + 1 autre (ou 1 cœur + 2 autres)
    const nbCoreVrais = Math.random() < 0.5 ? 2 : 1;
    const vrais = [
        ...randomChoiceMultiple(coreVrais, nbCoreVrais),
        ...randomChoiceMultiple(nonCoreVrais, 3 - nbCoreVrais),
    ];
    // Dédoublonner par texte
    const uniqueVrais = [];
    for (const v of vrais) {
        if (!uniqueVrais.some((u) => u.text === v.text)) uniqueVrais.push(v);
    }
    while (uniqueVrais.length < 3) {
        const cand = randomChoice(VRAI_POOL);
        if (!uniqueVrais.some((u) => u.text === cand.text)) uniqueVrais.push(cand);
    }

    // Sélection des faux : 1 égalité stricte + 1 autre
    const faux = [randomChoice(egaliteFaux), randomChoice(autreFaux)];
    // Dédoublonner
    while (new Set(faux.map((f) => f.text)).size < 2) {
        faux[1] = randomChoice(autreFaux);
    }

    const vraisTextes = uniqueVrais.slice(0, 3).map((v) => v.text);
    const fauxTextes = faux.map((f) => f.text);
    const allItems = [...vraisTextes, ...fauxTextes];

    return makeQcm(
        allItems,
        vraisTextes,
        `<p>Parmi les affirmations suivantes, sélectionne celles qui sont correctes.</p>`,
        opts(L_QCM_MULTI),
        true, // multi
    );
};

// ═══════════════════════════════════════════════════════════
// 3. SAISIE — "Donne l'arrondi au X de Y" (Y non-décimal)
// ═══════════════════════════════════════════════════════════

const genNonDecSaisie = () => {
    // Saisie libre : on tire sur tout le pool (pas de biais cœur — exo de pratique).
    // Les autres formats (QCM/VF/drag) gardent le biais Eduscol pour cibler π et 1/3.
    const nd = randomChoice(NON_DEC);
    const rang = randomChoice(RANGS);
    const correct = nd.arrondis[rang.key];

    const payload = makeSaisie(
        correct,
        `<p>On sait que {{element:0}} ≈ ${nd.dev4}…</p><p>Donne l'arrondi ${rang.libelle} de {{element:0}}.</p>`,
        { ...opts(L_SAISIE), strict: false },
    );
    payload.elements_additionnels = { version: 1, elements: [expr(nd.katex)] };
    return payload;
};

// ═══════════════════════════════════════════════════════════
// 4. VRAI/FAUX — 6 lignes 3V/3F avec égalité stricte obligatoire
// ═══════════════════════════════════════════════════════════

const genNonDecVF = () => {
    const lignes = [];
    const bonnes = []; // booleans
    const seen = new Set();
    const usedNds = new Set();

    const tryPush = (text, isVrai, nd) => {
        if (seen.has(text)) return false;
        seen.add(text);
        lignes.push(text);
        bonnes.push(isVrai);
        if (nd) usedNds.add(nd.name);
        return true;
    };

    // 1. Égalité stricte obligatoire (FAUX) — cas-clé conceptuel fiche ligne 224
    {
        const nd = pickCore();
        const rang = randomChoice([RANGS[1], RANGS[2]]);
        tryPush(`${symb(nd)} = ${nd.arrondis[rang.key]}`, false, nd);
    }

    // 2. Seconde égalité stricte (FAUX) sur un autre non-décimal
    {
        const remaining = NON_DEC.filter((x) => !usedNds.has(x.name));
        const nd = remaining.length ? randomChoice(remaining) : randomChoice(NON_DEC);
        const rang = randomChoice([RANGS[1], RANGS[2]]);
        tryPush(`${symb(nd)} = ${nd.arrondis[rang.key]}`, false, nd);
    }

    // 3. Arrondi correct cœur Eduscol (VRAI)
    {
        const nd = pickCore();
        const rang = randomChoice(RANGS);
        tryPush(`L'arrondi ${rang.libelle} de ${symb(nd)} est ${nd.arrondis[rang.key]}`, true, nd);
    }

    // 4. Test règle du 5 (VRAI ou FAUX par troncature)
    {
        const rulOf5 = [
            { nd: NON_DEC.find((x) => x.name === '2/3'), rang: 'centieme' },
            { nd: NON_DEC.find((x) => x.name === '1/6'), rang: 'centieme' },
            { nd: NON_DEC.find((x) => x.name === '2/3'), rang: 'dixieme' },
            { nd: NON_DEC.find((x) => x.name === '1/6'), rang: 'dixieme' },
        ];
        const pick = randomChoice(rulOf5);
        const rangObj = RANGS.find((r) => r.key === pick.rang);
        const isVrai = Math.random() < 0.5;
        let valeur;
        if (isVrai) {
            valeur = pick.nd.arrondis[pick.rang];
        } else {
            // Troncature (piège règle du 5)
            valeur = pick.rang === 'centieme' ? pick.nd.dev4.slice(0, 4) : pick.nd.dev4.slice(0, 3);
            if (valeur === pick.nd.arrondis[pick.rang]) {
                // pas de règle du 5 ici, forcer un faux via rang décalé
                valeur = pick.nd.arrondis.dixieme === pick.nd.arrondis[pick.rang]
                    ? pick.nd.arrondis.unite
                    : pick.nd.arrondis.dixieme;
            }
        }
        const text = `L'arrondi ${rangObj.libelle} de ${symb(pick.nd)} est ${valeur}`;
        tryPush(text, isVrai, pick.nd);
    }

    // 5-6. Compléter à 6 avec équilibrage 3V/3F
    let safety = 0;
    while (lignes.length < 6 && safety < 60) {
        safety++;
        const currentVrai = bonnes.filter((b) => b === true).length;
        const currentFaux = bonnes.filter((b) => b === false).length;
        let wantVrai;
        if (currentVrai < 3 && currentFaux >= 3) wantVrai = true;
        else if (currentFaux < 3 && currentVrai >= 3) wantVrai = false;
        else wantVrai = Math.random() < 0.5;

        const nd = randomChoice(NON_DEC);
        const rang = randomChoice(RANGS);
        let affichage;
        if (wantVrai) {
            affichage = nd.arrondis[rang.key];
        } else {
            // Faux : rang décalé ou troncature
            if (rang.key === 'centieme') {
                const trunc = nd.dev4.slice(0, 4);
                if (trunc !== nd.arrondis.centieme) {
                    affichage = trunc;
                } else {
                    affichage = nd.arrondis.dixieme;
                }
            } else {
                const other = randomChoice(RANGS.filter((r) => r.key !== rang.key));
                affichage = nd.arrondis[other.key];
            }
            if (affichage === nd.arrondis[rang.key]) continue;
        }
        tryPush(`L'arrondi ${rang.libelle} de ${symb(nd)} est ${affichage}`, wantVrai, nd);
    }

    // Assurer strict 3V/3F (correction finale rarissime si boucle a mal équilibré)
    while (lignes.length < 6) {
        lignes.push("L'arrondi au dixième de π est 3,1");
        bonnes.push(true);
    }

    // Rappel avec 4 développements max des non-décimaux utilisés
    const used = [...usedNds].slice(0, 4).map((name) => NON_DEC.find((x) => x.name === name));
    // Rappel en UNE phrase, fractions en style texte : la liste à puces avec
    // une grande fraction par ligne étirait l'énoncé (retour écran 2026-09-06).
    const rappel = enumFr(used.map((s) => `${symbInline(s)} ≈ ${s.dev4}…`));
    const texte = `<p>On rappelle que ${rappel}.</p><p>Pour chaque affirmation, dis si elle est vraie ou fausse.</p>`;

    const bonnesReponsesInt = bonnes.map((b) => (b ? 0 : 1));

    return makeVraiFaux(lignes, bonnesReponsesInt, texte, opts(L_VF));
};

// ═══════════════════════════════════════════════════════════
// 6. TEXTE À TROUS (DRAG) — 2 non-décimaux × 2 rangs = 4 trous
// ═══════════════════════════════════════════════════════════

const genNonDecTrouDrag = () => {
    // nd1 : cœur Eduscol (π ou 1/3)
    // nd2 : test règle du 5 (2/3 ou 1/6) distinct de nd1
    const nd1 = pickCore();
    const nd2Candidates = NON_DEC.filter(
        (x) => x !== nd1 && !CORE_EDUSCOL.includes(x.name),
    );
    const nd2 = randomChoice(nd2Candidates);

    const dix1 = nd1.arrondis.dixieme;
    const cent1 = nd1.arrondis.centieme;
    const dix2 = nd2.arrondis.dixieme;
    const cent2 = nd2.arrondis.centieme;

    const bonnes = [dix1, cent1, dix2, cent2];
    if (new Set(bonnes).size < 4) {
        // Collision de bonnes réponses : on fige vers couple canonique π + 2/3
        const p = NON_DEC.find((x) => x.name === 'π');
        const dt = NON_DEC.find((x) => x.name === '2/3');
        return buildTrouDragPayload(p, dt);
    }

    return buildTrouDragPayload(nd1, nd2);
};

const buildTrouDragPayload = (nd1, nd2) => {
    const dix1 = nd1.arrondis.dixieme;
    const cent1 = nd1.arrondis.centieme;
    const dix2 = nd2.arrondis.dixieme;
    const cent2 = nd2.arrondis.centieme;
    const bonnes = [dix1, cent1, dix2, cent2];

    const phrase = `L'arrondi au dixième de ${symb(nd1)} est {{${dix1}}} et son arrondi au centième est {{${cent1}}}. L'arrondi au dixième de ${symb(nd2)} est {{${dix2}}} et son arrondi au centième est {{${cent2}}}.`;

    // Distracteurs : 3 valeurs-pièges de familles distinctes
    const dist = new Set();
    // (a) Rang décalé de nd1 (unité)
    if (!bonnes.includes(nd1.arrondis.unite)) dist.add(nd1.arrondis.unite);
    // (b) Troncature centième nd2 (piège règle du 5)
    const trunc2 = nd2.dev4.slice(0, 4);
    if (!bonnes.includes(trunc2)) dist.add(trunc2);
    // (c) Troncature dixième nd2
    const trunc2Dix = nd2.dev4.slice(0, 3);
    if (!bonnes.includes(trunc2Dix)) dist.add(trunc2Dix);
    // Fallback : unité nd2, puis centième d'un autre non-dec
    if (dist.size < 3) {
        if (!bonnes.includes(nd2.arrondis.unite)) dist.add(nd2.arrondis.unite);
    }
    if (dist.size < 3) {
        for (const other of NON_DEC) {
            if (other === nd1 || other === nd2) continue;
            const v = other.arrondis.centieme;
            if (!bonnes.includes(v) && !dist.has(v)) {
                dist.add(v);
                if (dist.size >= 3) break;
            }
        }
    }

    const distArr = [...dist].slice(0, 3);

    const rappel = `<p>On rappelle que ${symbInline(nd1)} ≈ ${nd1.dev4}… et ${symbInline(nd2)} ≈ ${nd2.dev4}….</p>`;

    return makeTexteTrou(
        phrase,
        `${rappel}<p>Complète en faisant glisser les bonnes valeurs dans les cases.</p>`,
        { ...opts(L_TROU), mode: 'drag', distracteurs: distArr },
    );
};

// ═══════════════════════════════════════════════════════════
// 6. DROITE GRADUÉE + FRACTIONS — Template inspiré du manuel
// ═══════════════════════════════════════════════════════════
//
// L'élève lit une droite graduée [0 ; 1] au dixième où 4 fractions ont été
// placées (labellisées A, B, C, D). L'exercice teste deux compétences clés :
//   - Encadrer une fraction au dixième (saisie via texte à trous)
//   - Arrondir cette fraction au dixième (QCM ou Vrai/Faux)
//
// La droite sert d'outil visuel — l'élève n'a pas besoin de connaître les
// décimales exactes (1/3 ≈ 0,333...) : il lit la position sur la droite.

const L_DROITE_ENC = "Encadrer des fractions au dixième à l'aide d'une droite graduée";
const L_DROITE_ARR_QCM = "Arrondir une fraction au dixième à l'aide d'une droite graduée (QCM)";
const L_DROITE_ARR_VF = "Arrondir des fractions au dixième à l'aide d'une droite graduée (Vrai/Faux)";

// Pool de fractions dans [0 ; 1] avec encadrement au dixième non-trivial.
// Inclut décimales et non-décimales : la droite est l'outil commun.
const FRACTIONS_DROITE_POOL = [
    { num: 1, den: 3, value: 1/3 },    // ≈ 0,333 → [0,3 ; 0,4]
    { num: 2, den: 3, value: 2/3 },    // ≈ 0,666 → [0,6 ; 0,7]
    { num: 1, den: 4, value: 0.25 },   //          [0,2 ; 0,3]
    { num: 3, den: 4, value: 0.75 },   //          [0,7 ; 0,8]
    { num: 1, den: 6, value: 1/6 },    // ≈ 0,166 → [0,1 ; 0,2]
    { num: 5, den: 6, value: 5/6 },    // ≈ 0,833 → [0,8 ; 0,9]
    { num: 1, den: 7, value: 1/7 },    // ≈ 0,142 → [0,1 ; 0,2]
    { num: 2, den: 7, value: 2/7 },    // ≈ 0,285 → [0,2 ; 0,3]
    { num: 1, den: 8, value: 0.125 },  //          [0,1 ; 0,2]
    { num: 3, den: 8, value: 0.375 },  //          [0,3 ; 0,4]
    { num: 5, den: 8, value: 0.625 },  //          [0,6 ; 0,7]
    { num: 7, den: 8, value: 0.875 },  //          [0,8 ; 0,9]
    { num: 1, den: 9, value: 1/9 },    // ≈ 0,111 → [0,1 ; 0,2]
    { num: 2, den: 9, value: 2/9 },    // ≈ 0,222 → [0,2 ; 0,3]
    { num: 4, den: 9, value: 4/9 },    // ≈ 0,444 → [0,4 ; 0,5]
    { num: 5, den: 9, value: 5/9 },    // ≈ 0,555 → [0,5 ; 0,6]
];

// ─── Helpers droite fractions ───

const toFrDec = (n, dec) => n.toFixed(dec).replace('.', ',');

/** Encadrement au dixième + arrondi du dixième le plus proche. */
const encadrementDixieme = (v) => {
    const inf = Math.floor(v * 10) / 10;
    const sup = Math.round((inf + 0.1) * 1000) / 1000;
    const round = (v - inf) < (sup - v) ? inf : sup;
    return { inf, sup, round };
};

/** Choisit n fractions dans des dixièmes distincts pour avoir des encadrements uniques. */
const pickFractionsDistinctDixiemes = (n) => {
    const shuffled = shuffle(FRACTIONS_DROITE_POOL);
    const selected = [];
    const usedDix = new Set();
    for (const f of shuffled) {
        const key = Math.floor(f.value * 10);
        if (!usedDix.has(key)) {
            selected.push(f);
            usedDix.add(key);
            if (selected.length === n) break;
        }
    }
    return selected;
};

/** Construit les elements_additionnels : N expressions KaTeX + 1 droite. */
const buildDroiteFractionsElements = (selected, labels) => ({
    version: 1,
    elements: [
        ...selected.map((f) => expr(`\\dfrac{${f.num}}{${f.den}}`)),
        {
            type: 'numberline',
            config: {
                start: 0, end: 1, step: 0.1,
                lineType: 'half',
                showArrow: true,
                showStart: true,
                showEnd: true,
                points: selected.map((f, i) => ({
                    value: f.value,
                    label: labels[i],
                    showValue: false,
                    color: '#22c55e',
                })),
            },
        },
    ],
});

/** Construit la partie "intro" de l'énoncé : définitions des fractions + droite.
 *  Important : pas de <p> autour de la ligne `defs` — les éléments `expression`
 *  sont rendus inline-block et un <p> block dans un <span> casse le layout. */
const buildDroiteFractionsIntro = (selected, labels) => {
    const defs = selected.map((_, i) => `${labels[i]} = {{element:${i}}}`).join(' ; ');
    const droiteIdx = selected.length; // la droite est le dernier élément
    const nomNb = selected.length === 4 ? 'quatre' : 'trois';
    return `<p>Voici ${nomNb} points placés sur la demi-droite graduée ci-dessous :</p>${defs}.{{element:${droiteIdx}}}`;
};

// ─── Générateur A : Encadrement au dixième (texte à trous saisie) ───

const genDroiteFractionsEncadrement = () => {
    const selected = pickFractionsDistinctDixiemes(4).sort((a, b) => a.value - b.value);
    if (selected.length < 4) return genNonDecSaisie(); // fallback improbable
    const labels = ['A', 'B', 'C', 'D'];

    const elAdd = buildDroiteFractionsElements(selected, labels);
    const intro = buildDroiteFractionsIntro(selected, labels);
    const texte = `${intro}<p>Pour chaque point, donne un encadrement au dixième en lisant la droite.</p>`;

    const parts = selected.map((f, i) => {
        const { inf, sup } = encadrementDixieme(f.value);
        return `{{${toFrDec(inf, 1)}}} < ${labels[i]} < {{${toFrDec(sup, 1)}}}`;
    });
    const phrase = parts.join('\n');

    return makeTexteTrou(phrase, texte, {
        ...opts(L_DROITE_ENC),
        mode: 'saisie',
        strict: false,
        distracteurs: [],
        elements_additionnels: elAdd,
    });
};

// ─── Générateur B : Arrondi au dixième (QCM unique) ───

/** Fraction "QCM-safe" : visuellement clairement plus proche d'un des deux dixièmes. */
const isQcmSafe = (value) => {
    const nearest = Math.round(value * 10) / 10;
    return Math.abs(value - nearest) <= 0.035;
};

const genDroiteFractionsArrondiQcm = () => {
    // Retry jusqu'à obtenir une sélection contenant au moins 1 fraction "QCM-safe"
    let selected = null;
    let safeTargets = [];
    for (let attempt = 0; attempt < 10; attempt++) {
        const picked = pickFractionsDistinctDixiemes(4).sort((a, b) => a.value - b.value);
        if (picked.length < 4) continue;
        const safe = picked.filter((f) => isQcmSafe(f.value));
        if (safe.length > 0) {
            selected = picked;
            safeTargets = safe;
            break;
        }
    }
    if (!selected) return genNonDecQcm(); // fallback extrêmement improbable
    const labels = ['A', 'B', 'C', 'D'];

    const elAdd = buildDroiteFractionsElements(selected, labels);
    const intro = buildDroiteFractionsIntro(selected, labels);

    // Cible : pioche parmi les fractions "safe" uniquement
    const target = randomChoice(safeTargets);
    const targetIdx = selected.indexOf(target);
    const targetLabel = labels[targetIdx];
    const { inf, sup, round: targetRound } = encadrementDixieme(target.value);
    const correct = toFrDec(targetRound, 1);

    // Distracteurs : autre borne de l'encadrement + voisins
    const distractors = new Set();
    distractors.add(toFrDec(targetRound === inf ? sup : inf, 1));
    if (targetRound - 0.1 >= 0) distractors.add(toFrDec(Math.round((targetRound - 0.1) * 10) / 10, 1));
    if (targetRound + 0.1 <= 1) distractors.add(toFrDec(Math.round((targetRound + 0.1) * 10) / 10, 1));
    const distArr = [...distractors].filter((d) => d !== correct).slice(0, 3);

    const texte = `${intro}<p>Quel est l'arrondi au dixième du point ${targetLabel} ?</p>`;
    return makeQcm([correct, ...distArr], correct, texte, { ...opts(L_DROITE_ARR_QCM), elements_additionnels: elAdd });
};

// ─── Générateur C : Arrondi au dixième (Vrai/Faux) ───

const genDroiteFractionsArrondiVF = () => {
    const selected = pickFractionsDistinctDixiemes(4).sort((a, b) => a.value - b.value);
    if (selected.length < 4) return genNonDecVF(); // fallback improbable
    const labels = ['A', 'B', 'C', 'D'];

    const elAdd = buildDroiteFractionsElements(selected, labels);
    const intro = buildDroiteFractionsIntro(selected, labels);

    // 4 lignes V/F : 2 vraies (arrondi correct), 2 fausses (l'autre borne du dixième)
    const flags = shuffle([0, 0, 1, 1]); // 0=Vrai, 1=Faux
    const lignes = selected.map((f, i) => {
        const { inf, sup, round } = encadrementDixieme(f.value);
        const affiche = flags[i] === 0 ? round : (round === inf ? sup : inf);
        return `${labels[i]} ≈ ${toFrDec(affiche, 1)}`;
    });

    const texte = `${intro}<p>Pour chaque affirmation, dis si elle est vraie ou fausse.</p>`;
    return makeVraiFaux(lignes, flags, texte, { ...opts(L_DROITE_ARR_VF), elements_additionnels: elAdd });
};

// ─── Registre ───
const GENERATORS = {
    arrondi_qcm_unique: genNonDecQcm,
    arrondi_qcm_multiple: genNonDecQcmMultiple,
    arrondi_texte: genNonDecSaisie,
    arrondi_tableau_vf: genNonDecVF,
    arrondi_texte_trou_drag: genNonDecTrouDrag,
    droite_fractions_texte_trou_saisie: genDroiteFractionsEncadrement,
    droite_fractions_qcm_unique: genDroiteFractionsArrondiQcm,
    droite_fractions_tableau_vf: genDroiteFractionsArrondiVF,
};

// ─── Catégories ───
const FORMAT_LABELS = {
    qcm_unique: 'QCM', qcm_multiple: 'QCM Multi', texte: 'Saisie',
    texte_trou_drag: 'Drag', texte_trou_saisie: 'Saisie trous',
    relier: 'Relier', tableau_vf: 'Vrai/Faux', classer: 'Classer', ordonner: 'Ordonner',
};

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'arrondi', label: 'Arrondir un non-décimal à un rang demandé', formatsList: buildFormats('arrondi') },
    { id: 'droite_fractions', label: 'Encadrer et arrondir des fractions avec une droite graduée', formatsList: buildFormats('droite_fractions') },
];

// ─── Point d'entrée ───
export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
